// @givmo/app-admin — payment-config sourcing for the card form (07-1.3).
//
// The Shift4 publishable key is CONFIG, not code: it comes from the backend `GET /public/payments/
// config` endpoint (`paymentsGetPaymentsConfig` → `PaymentsConfigOut.publishable_key`), never baked
// into the bundle. This mirrors the legacy webapp's best-effort posture (`PaymentConfigService.
// getPublishableKey` → `web_view_handler` `?pk=`): the key resolves once, lazily; a fetch failure
// yields `null` and the card form simply reports "not ready" rather than mounting a mis-keyed Shift4
// instance. `useShift4(null)` stays idle until a key arrives, so a null key never tokenizes.
//
// Uses the generated `paymentsGetPaymentsConfigOptions()` TanStack Query factory (the app-wide
// api-client interceptor stack — Bearer + App Check + correlation id — applies), gated on `enabled`
// so the config is only fetched when the card form is actually about to render (the Add dialog opens).

import { useQuery } from '@tanstack/react-query';
import { paymentsGetPaymentsConfigOptions } from '@givmo/api-client/query';

export interface UsePaymentConfigResult {
  /** The Shift4 publishable key, or `null` until it resolves / on any failure. */
  publishableKey: string | null;
  /** True while the config request is in flight. */
  isLoading: boolean;
  /** True when the config could not be sourced (network/non-2xx) — the form is unavailable. */
  isError: boolean;
}

/**
 * Best-effort fetch of the per-env payment config, exposing just the publishable key.
 *
 * @param enabled - Only fetch when true (the caller enables it when the card form is about to
 *   render). Defaults to `true`.
 */
export function usePaymentConfig(enabled = true): UsePaymentConfigResult {
  const query = useQuery({
    ...paymentsGetPaymentsConfigOptions(),
    enabled,
    // Config is stable per-env; cache it for the session so re-opening the Add dialog is instant.
    staleTime: Infinity,
    retry: false,
  });

  const publishableKey =
    typeof query.data?.publishable_key === 'string' && query.data.publishable_key.length > 0
      ? query.data.publishable_key
      : null;

  return {
    publishableKey,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
